// views/RoomView.jsx: M3-8 routing shell.
//
// Layout rules:
//   • In the lobby (no room yet): show the full IdentityPanel above the
//     create/join CTAs so the user can pick a name + color first.
//   • Once the user is in a room (or playing a game): collapse the
//     identity surface into a compact badge in the top-right and let
//     the room/game UI take the full canvas.

import { useState, useEffect } from 'preact/hooks';
import '../styles/room.css';
import { SettingsPanel, SettingsGearButton } from '../ui/SettingsPanel.jsx';
import { useSettings } from '../ui/useSettings.js';
import { IdentityBadge } from '../components/IdentityBadge.jsx';
import { HeroAnimation } from '../components/HeroAnimation.jsx';
import { HowToPlayTrigger } from '../nav/HowToPlayTrigger.jsx';
import { RoomLobby } from '../components/RoomLobby.jsx';
import { initialLobbyStep } from '../components/lobby-flow.js';
import { copyrightText, shouldShowLobbyFooter } from '../components/footer-helpers.js';
import { useRoomWs } from '../hooks/useRoomWs.js';
import { setHowToPlayRoomSettings, resetHowToPlayRoomSettings } from '../nav/how-to-play-settings.js';

export function RoomView() {
  const ws = useRoomWs();
  const inRoom = ws.status === 'in_room' && !!ws.roomCode;
  // B2-10: publish the live room settings for the How-to-Play overlay
  // (it mounts above this view, in App). Outside a room the defaults
  // (target 10, stick on) apply.
  useEffect(() => {
    if (inRoom) {
      setHowToPlayRoomSettings({
        targetScore: ws.settings.target_score,
        stickTheDealer: ws.settings.stick_the_dealer,
      });
    } else {
      resetHowToPlayRoomSettings();
    }
  }, [inRoom, ws.settings.target_score, ws.settings.stick_the_dealer]);
  const isPlaying = inRoom && ws.phase === 'in_game';
  // Mirror RoomLobby's wizard step so the hero only shows on the 'choose'
  // landing screen, the create/join steps need the full height for the
  // identity panel + join form (no scrolling).
  const [lobbyStep, setLobbyStep] = useState(() => initialLobbyStep(ws.initialJoinCode));
  // T7, settings reachable from the lobby shell, not just in-game.
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [settings, updateSettings] = useSettings();

  return (
    <div
      // Restyle 3A: "cream" scopes cream-screens.css to the home and room
      // screens; the game screen (phase in_game) keeps its own look.
      class={ws.phase === 'in_game' ? 'room-view' : 'room-view cream'}
      data-testid="room-view"
      data-in-room={inRoom ? 'true' : 'false'}
      data-phase={ws.phase ?? null}
    >
      {!isPlaying && (
        <header class="room-header" data-testid="room-header" data-mode={inRoom ? 'compact' : 'full'}>
          {/* Restyle 3A: the cream mockup's eyebrow line above the title. */}
          {!inRoom && <p class="room-eyebrow">Four seats. One trump.</p>}
          <h1 class="room-title" data-testid="room-title">euchre.dev</h1>
          {!inRoom && (
            <p class="room-subtitle">Play Euchre online with friends.</p>
          )}
          {/* #5b, In-room only: the compact "?" trigger beside the identity
              badge. On home the How-to-Play link lives below the lobby CTAs
              (#4), so the easy-to-miss corner "?" is gone there. Opening the
              overlay never unmounts this view. */}
          <div class="room-identity-corner">
            {inRoom && <HowToPlayTrigger />}
            <SettingsGearButton variant="cream" onClick={() => setSettingsOpen(true)} />
            {inRoom && <IdentityBadge closeGlyph="X" />}
          </div>
          {/* #5a, looping canvas "gameplay demo" hero. Only on the home
              'choose' screen (not the create/join steps, which need the
              vertical space for the form, and never in a room/game) so the
              rAF loop stays cheap. Decorative + reduced-motion aware. */}
          {!inRoom && lobbyStep === 'choose' && <HeroAnimation />}
        </header>
      )}
      <SettingsPanel
        open={settingsOpen}
        settings={settings}
        onUpdate={updateSettings}
        onClose={() => setSettingsOpen(false)}
      />
      <main class="room-body" data-mode={isPlaying ? 'game' : 'normal'}>
        <RoomLobby ws={ws} onStepChange={setLobbyStep} />
      </main>
      {/* #3: small copyright footer on the lobby/home shell. Hidden in the
          in-game full-canvas takeover so it never overlaps the table. */}
      {shouldShowLobbyFooter(isPlaying) && (
        <footer class="room-footer" data-testid="room-footer">
          {copyrightText()}
        </footer>
      )}
    </div>
  );
}
